import { storeToRefs } from "pinia";
import { computed } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import features from "shared/services/features";
import { useOrganisationFeaturesStore } from "shared/stores/organisationFeatures";
import { useSocialIntegrationsStore } from "shared/stores/socialIntegrations";
import { useUserStore } from "shared/stores/user";

export default function useFeatureWithCredits(
  options: {
    feature?: string;
    minimumCredits?: number;
  } = {}
) {
  const { feature = "has_social_engage", minimumCredits = 1 } = options;

  const socialIntegrationsStore = useSocialIntegrationsStore();
  const { integrations, refreshedAt } = storeToRefs(socialIntegrationsStore);

  const { organisation } = storeToRefs(useUserStore());

  const { refreshUsedCredits } = useOrganisationFeaturesStore();

  const { socialEngage } = storeToRefs(useOrganisationFeaturesStore());

  const isUnlimitedEngageCredits = computed<boolean>(
    () => socialEngage.value?.credits === null
  );

  const engageAccountQuota = computed<number>(
    () => socialEngage.value?.credits ?? 0
  );

  const disableAddAccount = computed<boolean>(
    () =>
      !isUnlimitedEngageCredits.value &&
      integrations.value.length >= engageAccountQuota.value
  );

  const remainingQuota = computed<number>(
    () => engageAccountQuota.value - integrations.value.length
  );

  const integrationCount = computed<number>(() => integrations.value.length);

  const shouldShowCapAlert = computed<boolean>(
    () => integrationCount.value >= engageAccountQuota.value
  );

  async function getCredits(): Promise<number | false> {
    if (!features.has(feature)) return false;

    await refreshUsedCredits({
      organisationId: organisation.value.id,
      feature,
      handleErrors: () => {},
    });

    const credits = await features.getCredits(feature);

    return credits;
  }

  async function canUseFeature(): Promise<boolean> {
    let featureCredits: number | false = 0;

    try {
      featureCredits = await getCredits();
    } catch (error) {
      ErrorDialog(
        getLocaleText("social_engage_compose.unable_to_load_credits"),
        error
      );
    }

    return featureCredits === null || Number(featureCredits) > minimumCredits;
  }

  return {
    disableAddAccount,
    engageAccountQuota,
    isUnlimitedEngageCredits,
    remainingQuota,
    socialIntegrationsStoreRefreshedAt: refreshedAt,
    integrationCount,
    shouldShowCapAlert,

    canUseFeature,
    getCredits,
  };
}
